<template>
  <View direction="column" gap="xs">
    <CheckboxCard
      title="Require ID verification"
      description="Know who's touring your property. Renters pass a government-ID and selfie check before any tour — in-person, virtual or self-guided."
      icon="user-check-01"
      v-model="model"
    >
      <template #title-suffix>
        <!-- "Recommended" nudges the decision the owner is actually making here, and unlike a
             "New" badge it does not go stale and need removing in a few months. -->
        <Badge label="Recommended" variant="orange" size="small" />
      </template>

      <!-- Price belongs on the card: it is part of the decision, not a detail behind a click.
           Plain text only — every click inside the card toggles the checkbox. -->
      <View direction="row" gap="xs" align="center" wrap="wrap">
        <Text variant="label-medium" as="span">$1.50</Text>
        <Text variant="body-medium" color="secondary">per verification</Text>
      </View>
    </CheckboxCard>

    <!-- Outside the card on purpose: everything inside it toggles the checkbox. -->
    <Button class="explainer-trigger" variant="ghost" size="small" @click="explainerModalRef?.show()">
      <Icon name="question-mark-circle" size="extra-small" />
      <span>How ID verification works</span>
    </Button>
  </View>

  <Modal ref="explainerModalRef" position="center" width="34rem">
    <template #header>ID verification</template>
    <template #body>
      <View direction="column" gap="lg">
        <View direction="column" gap="xs">
          <Text variant="h5">Nobody tours your property unless you know who they are.</Text>
          <Text variant="body-medium" color="secondary">
            Every tour means meeting a stranger — or handing one a door code. This turns that stranger into a verified,
            on-the-record person, for $1.50.
          </Text>
        </View>

        <View direction="column" gap="sm">
          <Text variant="label-medium">What we check</Text>
          <View direction="column" gap="md">
            <View v-for="check of checks" :key="check.title" direction="row" gap="md" align="center">
              <IconTile :icon="check.icon" :color="check.color" size="small" colored-icon />
              <View direction="column">
                <Text variant="label-medium" as="span">{{ check.title }}</Text>
                <Text variant="body-medium" color="secondary">{{ check.detail }}</Text>
              </View>
            </View>
          </View>
        </View>

        <View direction="column" gap="sm" class="timing">
          <View direction="row" gap="sm" align="center">
            <Icon name="clock-fast-forward" size="small" />
            <Text variant="label-medium">Asked at the right moment — not on hello</Text>
          </View>
          <Text variant="body-medium" color="secondary">
            Demanding ID upfront kills good leads. Tallo keeps the early conversation normal and only asks once a tour
            is booked, so browsers never hit a wall and only serious renters verify. Renters already verified with Tallo
            skip it entirely.
          </Text>
        </View>

        <Hint icon="check-circle">
          Takes a renter about a minute on their phone. Applies to every tour type you offer: they're asked once the
          tour is booked and reminded before it. A self-guided renter gets the door code only once they verify. An
          in-person or virtual showing is released if they never do.
        </Hint>
      </View>
    </template>
    <template #footer>
      <Button @click="explainerModalRef?.close()">Got it</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { Badge, Button, CheckboxCard, Hint, Icon, IconTile, Modal, Text, View } from '@tallo/design-system';

/** Mirrors the Didit workflow Tallo runs: OCR + LIVENESS + FACE_MATCH + IP_ANALYSIS. */
const checks = [
  {
    icon: 'id-card',
    color: 'blue',
    title: 'A real government ID',
    detail: 'License, passport or ID — checked for tampering and expiry.',
  },
  {
    icon: 'face-id',
    color: 'violet',
    title: 'A live human, not a photo',
    detail: 'A printed picture, a screen replay or a deepfake fails the check.',
  },
  {
    icon: 'image-user-check',
    color: 'green',
    title: 'The same person as the ID',
    detail: 'Their selfie is matched against the portrait on the document.',
  },
  {
    icon: 'globe-05',
    color: 'orange',
    title: 'Where they really are',
    detail: 'A VPN or proxy hiding their location is flagged.',
  },
] as const;

const model = defineModel<boolean | null | undefined>();
const explainerModalRef = ref<InstanceType<typeof Modal>>();
</script>

<style scoped lang="scss">
.explainer-trigger {
  // A link, not a button: it explains the option rather than acting on it.
  align-self: flex-start;
  color: var(--t-color-neutral-600);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--t-color-neutral-950);
  }
}

.timing {
  padding: var(--t-spacing-md);
  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-md);
}
</style>
